import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDistanceToNow } from 'date-fns';

const InboxFeedPanel = ({ notifications, loading, error, selectedItems, onSelectionChange, onRefresh }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50 dark:bg-red-900/10';
      case 'urgent': case'high':
        return 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/10';
      case 'medium':
        return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10';
      default:
        return 'border-l-gray-300 dark:border-l-slate-600';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'payment':
        return 'DollarSign';
      case 'compliance':
        return 'Shield';
      case 'support':
        return 'MessageSquare';
      case 'billing':
        return 'FileText';
      default:
        return 'Bell';
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems?.includes(id)) {
      onSelectionChange(selectedItems?.filter(item => item !== id));
    } else {
      onSelectionChange([...selectedItems, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === notifications?.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(notifications?.map(n => n?.id));
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Icon name="Loader" size={32} className="animate-spin text-blue-600 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-400">Loading notifications...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <Icon name="AlertCircle" size={32} className="text-red-600 mx-auto mb-3" />
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!notifications || notifications?.length === 0) {
    return (
      <div className="p-12 text-center">
        <Icon name="Inbox" size={48} className="text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-400">No notifications found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Select All Header */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedItems?.length === notifications?.length}
            onChange={handleSelectAll}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Select All ({notifications?.length})
          </span>
        </label>
      </div>

      {/* Notification List */}
      <div className="divide-y divide-gray-200 dark:divide-slate-700 max-h-[600px] overflow-y-auto">
        {notifications?.map(notification => (
          <div
            key={notification?.id}
            className={`p-4 border-l-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${
              getSeverityColor(notification?.severity)
            } ${!notification?.readAt ? 'font-medium' : ''}`}
          >
            <div className="flex items-start gap-4">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedItems?.includes(notification?.id)}
                onChange={() => handleSelectItem(notification?.id)}
                className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />

              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center border border-gray-200 dark:border-slate-600">
                  <Icon name={getCategoryIcon(notification?.category)} size={18} className="text-gray-600 dark:text-gray-400" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                        {notification?.category}
                      </span>
                      {notification?.clientName && (
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {notification?.clientName}
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm text-gray-900 dark:text-white mb-1">
                      {notification?.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                      {notification?.message}
                    </p>
                    {notification?.amount && (
                      <p className="text-sm font-semibold text-gray-900 dark:text-white mt-2">
                        Amount: K{notification?.amount?.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDistanceToNow(new Date(notification?.createdAt), { addSuffix: true })}
                    </span>
                    {!notification?.readAt && (
                      <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                    )}
                  </div>
                </div>

                {/* Action Link */}
                {notification?.actionUrl && (
                  <div className="mt-3">
                    <a
                      href={notification?.actionUrl}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      View Details
                      <Icon name="ExternalLink" size={14} />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InboxFeedPanel;